import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Plus, CheckCircle2, Clock, XCircle, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { CoPromoterSuggestionDialog } from './CoPromoterSuggestionDialog';

// Read-only renderer of the promoter's client form for co-promoters.
// Each text field shows an "Añadir contenido" button that opens a suggestion
// dialog; each list field shows the existing items plus an "Añadir nuevo/a"
// button. The co-promoter never mutates the promoter's data — everything goes
// through the suggestions collection on the backend.

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatValue = (val) => {
  if (val === null || val === undefined || val === '') return '';
  if (typeof val === 'object') {
    try {
      return Object.entries(val).filter(([k]) => !k.startsWith('_') && k !== 'photos').map(([k, v]) =>
        v ? `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}` : null
      ).filter(Boolean).join(' · ');
    } catch {
      return JSON.stringify(val);
    }
  }
  return String(val);
};

const isInlineListField = (f) =>
  f && (f.type === 'inline_repeatable' || f.type === 'repeatable' || f.type === 'photos_with_caption' || f.type === 'anecdote_list');

const isPhotoChildField = (c) =>
  ['photos_with_caption', 'photos', 'photo', 'protagonist_photo'].includes(c?.type);

export const CoPromoterReadOnlyForm = ({ token, recipientName }) => {
  const [template, setTemplate] = useState(null);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [dialogState, setDialogState] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchAll = useCallback(async () => {
    try {
      const [formRes, sugRes] = await Promise.all([
        axios.get(`${API_URL}/api/co-promote/${token}/client-form`),
        axios.get(`${API_URL}/api/co-promote/${token}/suggestions`),
      ]);
      setTemplate(formRes.data.template);
      setData(formRes.data.data || {});
      setSuggestions(sugRes.data.suggestions || []);
      // Expand the first section by default
      if (formRes.data.template?.sections?.[0]) {
        setExpanded({ [formRes.data.template.sections[0].id]: true });
      }
    } catch {
      toast.error('No se pudo cargar el formulario');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onSubmitted = (suggestion) => {
    setSuggestions(prev => [...prev, suggestion]);
  };

  const retractSuggestion = async (sid) => {
    if (!window.confirm('¿Retirar esta aportación?')) return;
    try {
      await axios.delete(`${API_URL}/api/co-promote/${token}/suggestions/${sid}`);
      setSuggestions(prev => prev.filter(s => s.suggestion_id !== sid));
      toast.success('Aportación retirada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo retirar');
    }
  };

  const submitForReview = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/co-promote/${token}/submit-for-review`);
      toast.success(`Enviado. ${res.data.pending_suggestions} aportación/es esperan al promotor.`);
      // Refresh status so the UI reflects the "already submitted" state.
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar al promotor');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-10"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (!template) return null;

  const pendingCount = suggestions.filter(s => s.status === 'pending' && s.is_own).length;
  const approvedCount = suggestions.filter(s => s.status === 'approved' && s.is_own).length;
  const rejectedCount = suggestions.filter(s => s.status === 'rejected' && s.is_own).length;

  // Group suggestions for quick inline lookup: by section → by fieldId (or
  // the sentinel '__section__' for list_items that target the section itself)
  const suggestionsByField = {};
  suggestions.forEach(s => {
    if (s.status === 'rejected') return; // hide rejected ones inline
    const sec = s.section_id || '__unknown__';
    const fid = s.field_id || '__section__';
    if (!suggestionsByField[sec]) suggestionsByField[sec] = {};
    if (!suggestionsByField[sec][fid]) suggestionsByField[sec][fid] = [];
    suggestionsByField[sec][fid].push(s);
  });

  // Render helpers ---------------------------------------------------------

  const renderInlineSuggestions = (sectionId, fieldId) => {
    const bucket = (suggestionsByField[sectionId] || {})[fieldId || '__section__'] || [];
    if (bucket.length === 0) return null;
    return (
      <div className="mt-2 space-y-1.5" data-testid={`inline-suggestions-${sectionId}-${fieldId || 'section'}`}>
        {bucket.map(s => (
          <InlineSuggestionCard key={s.suggestion_id} s={s} onRetract={retractSuggestion} />
        ))}
      </div>
    );
  };

  const renderFieldContent = (sectionId, f, sectionData, sectionTitle) => {
    if (!f || f.type === 'info') return null;

    // Render value + CTA per field type
    const valueRaw = sectionData?.[f.id];
    const label = f.label || f.id;

    // ---- Inline list field (anecdote_photo, stage_photos, etc.) ----
    if (isInlineListField(f) || f.type === 'inline_repeatable' || f.type === 'repeatable_inline') {
      const items = Array.isArray(valueRaw) ? valueRaw : [];
      const childFields = f.fields || f.item_fields || [];
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label label={label} helper={f.helper} />
          </div>
          {items.length > 0 ? (
            <ul className="text-xs text-muted-foreground space-y-1 pl-4 list-disc">
              {items.map((it, idx) => (
                <li key={idx} className="whitespace-pre-wrap">{formatValue(it)}</li>
              ))}
            </ul>
          ) : (
            <p className="text-xs italic text-muted-foreground">— Sin elementos aún —</p>
          )}
          <Button
            size="sm"
            variant="outline"
            className="gap-1 h-7 text-xs"
            onClick={() => setDialogState({
              mode: 'add_list_item',
              sectionId,
              sectionTitle,
              fieldId: f.id,
              fieldLabel: label,
              listItemFields: (childFields.length ? childFields : [{ id: 'text', label: 'Texto', multiline: true }])
                .filter(c => !isPhotoChildField(c))
                .map(c => ({
                id: c.id,
                label: c.label || c.id,
                placeholder: c.placeholder || '',
                multiline: c.type === 'textarea' || c.type === 'long_text' || !!c.multiline,
                options: c.options || null,
              })),
            })}
            data-testid={`add-list-${sectionId}-${f.id}`}
          >
            <Plus className="w-3 h-3" /> Añadir nuevo/a
          </Button>
          {renderInlineSuggestions(sectionId, f.id)}
        </div>
      );
    }

    // ---- Text / textarea / short input ----
    const valueIsObject = valueRaw && typeof valueRaw === 'object' && !Array.isArray(valueRaw);
    // Fields that are clearly not free-text (photo slots, dicts, etc.) should
    // not expose an "Añadir contenido" button because the backend cannot
    // merge a text value into them without overwriting structured data.
    const isTextualField = !valueIsObject && f.type !== 'photos_with_caption' && f.type !== 'photo' && f.type !== 'file' && !(typeof f.id === 'string' && f.id.startsWith('_'));
    const textValue = typeof valueRaw === 'string' ? valueRaw : (valueIsObject ? '' : formatValue(valueRaw));
    return (
      <div className="space-y-1.5">
        <Label label={label} helper={f.helper} />
        {textValue ? (
          <p className="text-sm text-foreground/80 whitespace-pre-wrap bg-muted/20 rounded-md p-2.5 border border-muted">
            {textValue}
          </p>
        ) : (
          <p className="text-xs italic text-muted-foreground bg-muted/20 rounded-md p-2.5 border border-muted">
            — {valueIsObject ? 'Este campo contiene un archivo o datos estructurados que no se pueden editar desde aquí.' : 'El promotor aún no ha rellenado este campo.'} —
          </p>
        )}
        {isTextualField && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1 h-7 text-xs"
            onClick={() => setDialogState({
              mode: 'add_text',
              sectionId,
              sectionTitle,
              fieldId: f.id,
              fieldLabel: label,
              currentValue: textValue,
            })}
            data-testid={`add-text-${sectionId}-${f.id}`}
          >
            <Plus className="w-3 h-3" /> Añadir contenido
          </Button>
        )}
        {renderInlineSuggestions(sectionId, f.id)}
      </div>
    );
  };

  const renderSection = (section) => {
    const sectionData = data[section.id] || {};
    const isOpen = !!expanded[section.id];
    const sectionSuggestions = suggestions.filter(s => s.section_id === section.id);

    // Handle repeatable section (e.g. milestones as a top-level list)
    if (section.repeatable && !section.fields?.some(f => f.type && f.type !== 'info')) {
      const items = Array.isArray(data[section.id]) ? data[section.id] : [];
      return (
        <Card className="card-elevated" key={section.id}>
          <CardHeader className="pb-3 cursor-pointer" onClick={() => setExpanded(p => ({ ...p, [section.id]: !p[section.id] }))}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                {section.title}
                {sectionSuggestions.length > 0 && (
                  <Badge variant="secondary" className="text-xs">{sectionSuggestions.length} aportación/es</Badge>
                )}
              </CardTitle>
              {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </div>
          </CardHeader>
          {isOpen && (
            <CardContent className="pt-0 space-y-3">
              {section.description && <p className="text-xs text-muted-foreground">{section.description}</p>}
              {items.length > 0 ? (
                <ul className="text-xs space-y-1 pl-4 list-disc text-muted-foreground">
                  {items.map((it, i) => <li key={i} className="whitespace-pre-wrap">{formatValue(it)}</li>)}
                </ul>
              ) : (
                <p className="text-xs italic text-muted-foreground">— Todavía no hay elementos —</p>
              )}
              <Button
                size="sm"
                variant="outline"
                className="gap-1 h-7 text-xs"
                onClick={() => setDialogState({
                  mode: 'add_list_item',
                  sectionId: section.id,
                  sectionTitle: section.title,
                  fieldLabel: section.title,
                  listItemFields: (section.item_fields || section.fields || [])
                    .filter(f => f.type !== 'info' && !isPhotoChildField(f))
                    .map(c => ({
                    id: c.id,
                    label: c.label || c.id,
                    placeholder: c.placeholder || '',
                    multiline: c.type === 'textarea' || c.type === 'long_text',
                    options: c.options || null,
                  })),
                })}
                data-testid={`add-repeat-${section.id}`}
              >
                <Plus className="w-3 h-3" /> Añadir nuevo/a
              </Button>
              {renderInlineSuggestions(section.id, null)}
            </CardContent>
          )}
        </Card>
      );
    }

    return (
      <Card className="card-elevated" key={section.id}>
        <CardHeader className="pb-3 cursor-pointer" onClick={() => setExpanded(p => ({ ...p, [section.id]: !p[section.id] }))}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              {section.title}
              {sectionSuggestions.length > 0 && (
                <Badge variant="secondary" className="text-xs">{sectionSuggestions.length} aportación/es</Badge>
              )}
            </CardTitle>
            {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </div>
          {section.description && isOpen && (
            <CardDescription className="text-xs mt-1">{section.description}</CardDescription>
          )}
        </CardHeader>
        {isOpen && (
          <CardContent className="pt-0 space-y-4">
            {(section.fields || []).filter(f => f.type !== 'info').map(f => (
              <div key={f.id}>{renderFieldContent(section.id, f, sectionData, section.title)}</div>
            ))}
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <Card className="bg-amber-50/60 border-amber-200">
        <CardContent className="p-4 text-sm text-amber-900 space-y-2">
          <p><strong>Estás en modo solo lectura.</strong> Puedes ver todo lo que el promotor ha escrito sobre{recipientName ? ` ${recipientName}` : ''} y añadir tus aportaciones.</p>
          <p>Nada de lo que envíes se añadirá al libro directamente: el promotor decidirá cuáles de tus aportaciones incorpora.</p>
          <div className="flex flex-wrap gap-3 pt-1">
            <Stat label="Pendientes" value={pendingCount} icon={<Clock className="w-3.5 h-3.5" />} />
            <Stat label="Aceptadas" value={approvedCount} icon={<CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />} />
            <Stat label="Descartadas" value={rejectedCount} icon={<XCircle className="w-3.5 h-3.5 text-red-500" />} />
          </div>
        </CardContent>
      </Card>

      {template.sections.map(renderSection)}

      <Card className="bg-primary/5 border-primary/30">
        <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-sm">
            <p className="font-medium">¿Has terminado de aportar?</p>
            <p className="text-xs text-muted-foreground">Al pulsar enviar, avisamos al promotor por email para que revise tus aportaciones.</p>
          </div>
          <Button
            onClick={submitForReview}
            disabled={submitting || pendingCount === 0}
            className="gap-2"
            data-testid="submit-for-review-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Enviar para revisión ({pendingCount})
          </Button>
        </CardContent>
      </Card>

      {dialogState && (
        <CoPromoterSuggestionDialog
          open={!!dialogState}
          onOpenChange={(v) => { if (!v) setDialogState(null); }}
          token={token}
          {...dialogState}
          onSubmitted={onSubmitted}
        />
      )}
    </div>
  );
};

// --------- tiny local atoms ---------
const Label = ({ label, helper }) => (
  <div className="flex items-baseline gap-2">
    <p className="text-sm font-medium">{label}</p>
    {helper && <span className="text-[11px] text-muted-foreground">{helper}</span>}
  </div>
);

const Stat = ({ label, value, icon }) => (
  <div className="flex items-center gap-1.5 text-xs bg-white/60 rounded-full px-2.5 py-1 border border-white">
    {icon} <span className="font-semibold">{value}</span> <span className="text-muted-foreground">{label}</span>
  </div>
);

const SuggestionRow = ({ s, onRetract }) => {
  const valueText = typeof s.value === 'string' ? s.value : formatValue(s.value);
  const badge = s.status === 'approved'
    ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Aceptada</Badge>
    : s.status === 'rejected'
    ? <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">Descartada</Badge>
    : <Badge variant="secondary" className="text-[10px]">Pendiente</Badge>;
  return (
    <div className="text-xs py-1.5 flex items-start gap-2">
      <div className="flex-1 min-w-0">
        <p className="text-foreground/80 line-clamp-2 whitespace-pre-wrap">{valueText}</p>
        {s.note && <p className="italic text-[11px] text-muted-foreground">Nota: {s.note}</p>}
      </div>
      <div className="flex items-center gap-1">
        {badge}
        {s.status === 'pending' && s.is_own && (
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onRetract(s.suggestion_id)} title="Retirar">
            <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Compact inline card shown right below a form field. Author name, status
// badge, and the aportación text. The co-promoter can retract only their OWN
// pending ones (the is_own flag comes from the backend).
const InlineSuggestionCard = ({ s, onRetract }) => {
  const valueText = typeof s.value === 'string' ? s.value : formatValue(s.value);
  const tone = s.status === 'approved'
    ? { ring: 'border-l-emerald-500 bg-emerald-50/60', badge: <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] gap-1"><CheckCircle2 className="w-2.5 h-2.5" /> Aceptada</Badge> }
    : { ring: 'border-l-amber-500 bg-amber-50/60', badge: <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] gap-1"><Clock className="w-2.5 h-2.5" /> Pendiente de validar</Badge> };
  return (
    <div className={`rounded-md border border-l-4 ${tone.ring} p-2 flex items-start gap-2 text-xs`}>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-foreground">{s.co_promoter_name}</span>
          {tone.badge}
          {s.is_own && <Badge variant="outline" className="text-[10px]">Tu aportación</Badge>}
        </div>
        <p className="text-foreground/80 whitespace-pre-wrap">{valueText}</p>
        {s.note && <p className="italic text-muted-foreground text-[11px]">Nota: {s.note}</p>}
      </div>
      {s.status === 'pending' && s.is_own && (
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onRetract(s.suggestion_id)} title="Retirar tu aportación">
          <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
        </Button>
      )}
    </div>
  );
};

export default CoPromoterReadOnlyForm;
